import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useConfig,
  useOrg,
  useRemoveLogo,
  useSetPositionCovers,
  useUpdateOrg,
  useUploadLogo,
} from '@/app/queries';
import { configLabel } from '@/lib/labels';
import { PositionChip } from '@/ui/Chip';
import { Button, Spinner } from '@/ui/Button';
import { Field, TextInput } from '@/ui/Field';
import { Notice, PageHeader, Panel } from '@/ui/Panel';
import { Billing } from '@/routes/settings/Billing';
import { TestCatalogue } from '@/routes/settings/TestCatalogue';

/**
 * The club itself.
 *
 * Small on purpose: a name, a crest, a colour and a line of small print. It
 * exists because a report with somebody else's branding on it does not get used,
 * and a coach who cannot hand a professional-looking page to a director is back
 * to a spreadsheet.
 */
export function Settings({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const org = useOrg();
  const update = useUpdateOrg();
  const upload = useUploadLogo();
  const removeLogo = useRemoveLogo();
  const fileInput = useRef<HTMLInputElement>(null);
  const config = useConfig();
  const setCovers = useSetPositionCovers();
  const [coverSaved, setCoverSaved] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [footer, setFooter] = useState('');
  const [timezone, setTimezone] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    const row = org.data;
    if (!row) return;
    setName(row.name);
    setColor(row.primaryColor ?? '');
    setFooter(row.reportFooter ?? '');
    setTimezone(row.timezone);
    setDirty(false);
  }, [org.data]);

  if (org.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  const logoUrl = org.data?.logoKey ? `/api/org/logo?v=${encodeURIComponent(org.data.logoKey)}` : null;

  return (
    <>
      <PageHeader
        title={t('settings.title')}
        lead={t('settings.lead')}
        help={localePath(locale, '/guide/club')}
      />

      <Panel
        action={
          <Button
            variant="primary"
            busy={update.isPending}
            disabled={!dirty || !name.trim()}
            onClick={() =>
              void update
                .mutateAsync({
                  name: name.trim(),
                  timezone: timezone || undefined,
                  primaryColor: color || null,
                  reportFooter: footer || null,
                })
                .then(() => {
                  setDirty(false);
                  setSaved(true);
                })
            }
          >
            {t('settings.save')}
          </Button>
        }
      >
        <div className="grid gap-x-5 gap-y-2 p-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label={t('settings.name')}>
            {(p) => (
              <TextInput
                {...p}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setDirty(true);
                  setSaved(false);
                }}
              />
            )}
          </Field>

          <Field label={t('settings.color')} optional hint={t('settings.colorHint')}>
            {(p) => (
              <span className="flex items-center gap-2">
                <input
                  type="color"
                  aria-label={t('settings.color')}
                  value={color || '#1f2937'}
                  onChange={(e) => {
                    setColor(e.target.value);
                    setDirty(true);
                    setSaved(false);
                  }}
                  className="h-11 w-14 shrink-0 cursor-pointer rounded-(--radius-control) border border-line bg-surface p-1"
                />
                <TextInput
                  {...p}
                  value={color}
                  placeholder="#1f2937"
                  onChange={(e) => {
                    setColor(e.target.value);
                    setDirty(true);
                    setSaved(false);
                  }}
                />
              </span>
            )}
          </Field>

          <Field label={t('settings.timezone')} optional>
            {(p) => (
              <TextInput
                {...p}
                value={timezone}
                onChange={(e) => {
                  setTimezone(e.target.value);
                  setDirty(true);
                  setSaved(false);
                }}
              />
            )}
          </Field>

          <div className="sm:col-span-2 lg:col-span-3">
            <Field label={t('settings.footer')} optional hint={t('settings.footerHint')}>
              {(p) => (
                <TextInput
                  {...p}
                  value={footer}
                  onChange={(e) => {
                    setFooter(e.target.value);
                    setDirty(true);
                    setSaved(false);
                  }}
                />
              )}
            </Field>
          </div>
        </div>

        <div className="border-t border-line p-4">
          <p className="font-condensed text-subhead font-semibold tracking-tight">
            {t('settings.logo')}
          </p>
          <p className="mt-1 text-meta text-ink-muted">{t('settings.logoHint')}</p>

          <div className="mt-3 flex flex-wrap items-center gap-4">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={t('settings.logo')}
                className="size-20 rounded-(--radius-control) border border-line bg-surface object-contain p-1"
              />
            ) : (
              <span className="grid size-20 place-items-center rounded-(--radius-control) border border-dashed border-line text-meta text-ink-faint">
                —
              </span>
            )}
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploadError(null);
                void upload
                  .mutateAsync(file)
                  .catch(() => setUploadError(t('settings.uploadFailed')))
                  .finally(() => {
                    if (fileInput.current) fileInput.current.value = '';
                  });
              }}
            />
            <Button
              variant="secondary"
              busy={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {t('settings.upload')}
            </Button>
            {logoUrl ? (
              <Button variant="quiet" onClick={() => void removeLogo.mutateAsync()}>
                {t('settings.removeLogo')}
              </Button>
            ) : null}
          </div>

          {uploadError ? (
            <div className="mt-3">
              <Notice tone="risk">{uploadError}</Notice>
            </div>
          ) : null}
        </div>

        {saved ? (
          <div className="border-t border-line px-4 py-3">
            <Notice tone="ok">{t('settings.saved')}</Notice>
          </div>
        ) : null}
      </Panel>

      <div className="mt-5">
        <Billing locale={locale} />

        {/* The shape of the club, beside its crest and its positions: the list
            every testing day afterwards is drawn from. */}
        <div className="mb-5">
          <TestCatalogue />
        </div>

        {/* Seventeen positions and what each can cover: set once a season. */}
        <Panel
          title={t('settings.positions')}
          collapsible
          storeKey="settings.positions"
          defaultOpen={false}
          summary={t('settings.positionsLead')}
        >
          <p className="px-4 pt-3 text-meta text-ink-muted">{t('settings.positionsLead')}</p>
          <ul className="divide-y divide-line">
            {(config.data?.positions ?? []).map((pos) => {
              const others = (config.data?.positions ?? []).filter((x) => x.key !== pos.key);
              const current = pos.covers ?? [];
              return (
                <li key={pos.key} className="flex flex-wrap items-start gap-3 px-4 py-3">
                  <span className="flex w-40 shrink-0 items-center gap-2">
                    <PositionChip code={pos.key} unit={pos.unit} />
                    <span className="truncate text-meta">{configLabel(pos, t)}</span>
                  </span>
                  {/*
                    Seventeen positions times sixteen others is 272 toggles, and
                    at a thumb's size that is some 2,700px of them on a screen a
                    coach visits once a season. So each position's covers are a
                    disclosure: closed it says who covers this one, open it is
                    the full set at full size. One row at a time, which is how
                    the question is asked anyway.
                  */}
                  <details className="min-w-0 flex-1">
                    <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 text-meta text-ink-faint">
                      <span>{t('settings.covers')}</span>
                      <span className="min-w-0 flex-1 truncate text-ink-muted">
                        {current.length > 0 ? current.join(' · ') : t('settings.coversNone')}
                      </span>
                    </summary>
                    <span className="mt-2 flex flex-wrap gap-1">
                      {others.map((other) => {
                        const on = current.includes(other.key);
                        return (
                          <button
                            key={other.key}
                            type="button"
                            aria-pressed={on}
                            onClick={() => {
                              const next = on
                                ? current.filter((k) => k !== other.key)
                                : [...current, other.key];
                              void setCovers
                                .mutateAsync({ positionKey: pos.key, covers: next })
                                .then(() => setCoverSaved(pos.key));
                            }}
                            className={`tabular grid min-h-11 min-w-11 place-items-center rounded-(--radius-control) border px-2 font-condensed text-meta transition-colors ${
                              on
                                ? 'border-ink bg-ink text-paper'
                                : 'border-line text-ink-faint hover:border-ink hover:text-ink'
                            }`}
                          >
                            {other.key}
                          </button>
                        );
                      })}
                    </span>
                  </details>
                  {coverSaved === pos.key ? (
                    <span className="text-meta text-ok">{t('settings.coversSaved')}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Panel>
      </div>
    </>
  );
}
